import React, { useMemo, useState, useEffect } from "react";
import standardBizApiClient from "../../util/BizApi/standardBizApiClient";
import "./surveyPopup.css";

export function SurveyModal({ open, onComplete, onSkip }) {
  const questions = useMemo(
    () => [
      {
        id: "certifications",
        title: "Which certification(s) are you preparing for?",
        type: "multi",
        options: ["CCSP", "CISA", "CISM", "CISSP", "CRISC", "SSCP", "Other"],
        hint: "You can select more than one",
        otherPlaceholder: "Other certification...",
      },
      {
        id: "existingCertifications",
        title: "Do you already have any of the following certifications?",
        type: "multi",
        options: [
          "CISSP",
          "CCSP",
          "SSCP",
          "CISM",
          "CISA",
          "CC",
          "CCSK",
          "GIAC/GSEC",
          "Security+",
          "CySA+",
          "A+",
          "Network+",
        ],
        hint: "You can select more than one",
      },
      {
        id: "examTimeline",
        title: "When are you planning to take the exam?",
        type: "select", // Will be rendered as radio now
        options: ["Within 30 days", "1–2 months", "3–6 months", "Not scheduled yet"],
      },
      {
        id: "prepIntent",
        title: "What best describes your current certification goal?",
        type: "multi",
        options: [
          "Check my exam readiness",
          "Improve weak domains",
          "Recommended by trainer/employer",
          "Exploring options",
        ],
        hint: "You can select more than one",
      },
      {
        id: "primaryFocus",
        title: "What is your primary focus at this stage of preparation?",
        type: "multi",
        options: [
          "Achieve exam readiness with confidence",
          "Optimize preparation time",
          "Strengthen specific domains",
          "Exploring options",
        ],
        hint: "You can select more than one",
      },
      {
        id: "experienceYears",
        title: "Professional experience (Years)",
        type: "select",
        options: ["0–3", "5–7", "10–15", "15+"],
      },
      {
        id: "currentRole",
        title: "Which best describes your current role?",
        type: "select",
        options: [
          "Security Engineer/Analyst",
          "GRC/Risk/Compliance",
          "Security Consultant",
          "Architect",
          "Security Manager",
          "C level (give designations)",
          "Other",
        ],
      },
      {
        id: "priorTraining",
        title: "Have you taken training for this certification before?",
        type: "single",
        options: [
          "Yes—Classroom/in-person",
          "Yes—Online/self paced",
          "Yes—Bootcamp/trainer led",
          "Self study",
          "No",
        ],
      },
      {
        id: "companySize",
        title: "Your current organization size",
        type: "select",
        options: ["<100 employees", "100–500", "500–5000", "5000+", "Prefer not to say"],
      },
    ],
    []
  );

  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const [answers, setAnswers] = useState({
    certifications: [],
    certificationsOtherText: "",
    existingCertifications: [],
    examTimeline: "",
    prepIntent: [],
    primaryFocus: [],
    experienceYears: "",
    currentRole: "",
    priorTraining: "",
    companySize: "",
  });

  // ✅ auto exit after 3 seconds
  useEffect(() => {
    if (!completed) return;
    const t = setTimeout(() => handleClose(), 3000);
    return () => clearTimeout(t);
  }, [completed]);

  if (!open) return null;

  const isQuestionAnswered = (question) => {
    const id = question.id;

    if (question.type === "single") return !!answers[id];
    if (question.type === "select") return !!answers[id];
    if (question.type === "text") return (answers[id] || "").trim().length > 0;

    if (question.type === "multi") {
      const arr = answers[id] || [];
      if (arr.length === 0) return false;

      if (id === "certifications" && arr.includes("Other")) {
        return (answers.certificationsOtherText || "").trim().length > 0;
      }
      return true;
    }
    return false;
  };

  const allAnswered = questions.every(isQuestionAnswered);

  const pickSingle = (qId, value) => setAnswers((p) => ({ ...p, [qId]: value }));
  // Reuse pickSingle for select since we are converting select to radio buttons
  const pickSelect = (qId, value) => setAnswers((p) => ({ ...p, [qId]: value }));

  const toggleMulti = (qId, value) => {
    setAnswers((p) => {
      const arr = p[qId] || [];
      const has = arr.includes(value);

      if (qId === "certifications" && value === "Other" && has) {
        return {
          ...p,
          certifications: arr.filter((x) => x !== value),
          certificationsOtherText: "",
        };
      }

      return { ...p, [qId]: has ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const setText = (key, value) => setAnswers((p) => ({ ...p, [key]: value }));

  const finish = async () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);
    try {
      // Submit to Backend
      await standardBizApiClient.survey_submit_response(answers);

      localStorage.setItem("surveyCompleted", "true");
      localStorage.setItem("surveyAnswers", JSON.stringify(answers));

      setCompleted(true);
    } catch (error) {
      console.error("Failed to submit survey", error);
      alert("Something went wrong saving your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => onComplete?.(answers);

  const handleSkip = () => onSkip?.();


  if (completed) {
    return (
      <div className="wp-survey-overlay" role="dialog" aria-modal="true">
        <div className="wp-survey-card">
          <div className="wp-survey-top">
            <div className="wp-survey-brand">Wanna Practice</div>
          </div>
          <div className="wp-survey-header-center">
            <div className="wp-survey-survey">SURVEY</div>
            <div className="wp-survey-description">This survey helps us improve our product and deliver better service</div>
          </div>

          <div className="wp-survey-body wp-survey-complete-body">
            <div className="wp-survey-tick">✓</div>
            <h2 className="wp-survey-title wp-survey-title-center">
              Thanks for your feedback!
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wp-survey-overlay" role="dialog" aria-modal="true">
      <div className="wp-survey-card">
        <div className="wp-survey-top">
          <div className="wp-survey-brand">Wanna Practice</div>
        </div>
        <div className="wp-survey-header-center">
          <div className="wp-survey-survey">SURVEY</div>
          <div className="wp-survey-description">This survey helps us improve our product and deliver better service</div>
        </div>

        <div className="wp-survey-body">
          {questions.map((q, index) => (
            <div key={q.id} className="wp-survey-question-block">
              <h2 className="wp-survey-title">
                {index + 1}. {q.title}
              </h2>
              {q.hint ? <div className="wp-survey-hint">{q.hint}</div> : null}

              {/* SINGLE */}
              {q.type === "single" && (
                <div className="wp-survey-options-grid">
                  {q.options.map((opt) => {
                    const selected = answers[q.id] === opt;
                    return (
                      <label key={opt} className={`wp-survey-check-row ${selected ? "selected" : ""}`}>
                        <input
                            type="radio"
                            name={q.id}
                            checked={selected}
                            onChange={() => pickSingle(q.id, opt)}
                        />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* SELECT converted to Radio */}
              {q.type === "select" && (
                <div className="wp-survey-options-grid">
                   {q.options.map((opt) => {
                    const selected = answers[q.id] === opt;
                    return (
                      <label key={opt} className={`wp-survey-check-row ${selected ? "selected" : ""}`}>
                         <input
                            type="radio"
                            name={q.id}
                            checked={selected}
                            onChange={() => pickSelect(q.id, opt)}
                        />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* MULTI */}
              {q.type === "multi" && (
                <div className="wp-survey-options-grid">
                  {q.options.map((opt) => {
                    const checked = (answers[q.id] || []).includes(opt);

                    // ✅ Other inline input on right
                    if (q.id === "certifications" && opt === "Other") {
                      return (
                        <div key={opt} className="wp-survey-check-row wp-survey-check-row-split">
                          <label className="wp-survey-check-left">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMulti(q.id, opt)}
                            />
                            <span>{opt}</span>
                          </label>

                          {checked ? (
                            <input
                              className="wp-survey-inline-input"
                              type="text"
                              value={answers.certificationsOtherText}
                              onChange={(e) => setText("certificationsOtherText", e.target.value)}
                              placeholder={q.otherPlaceholder || "Other..."}
                            />
                          ) : null}
                        </div>
                      );
                    }

                    return (
                      <label key={opt} className="wp-survey-check-row">
                        <input type="checkbox" checked={checked} onChange={() => toggleMulti(q.id, opt)} />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* TEXT */}
              {q.type === "text" && (
                <input
                  className="wp-survey-input"
                  type="text"
                  value={answers[q.id]}
                  onChange={(e) => setText(q.id, e.target.value)}
                  placeholder={q.placeholder || "Type here..."}
                />
              )}
            </div>
          ))}
        </div>

        <div className="wp-survey-footer wp-survey-footer-center">
            <button
               type="button"
               onClick={handleSkip}
               className="wp-survey-secondary"
               style={{ marginRight: '12px' }}
            >
                Skip
            </button>
            <button
              type="button"
              onClick={finish}
              disabled={!allAnswered || submitting}
              className="wp-survey-primary"
            >
              {submitting ? "Submitting..." : "Submit Survey"}
            </button>
        </div>
      </div>
    </div>
  );
}

export default function SurveyGate({ isLoggedIn }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setOpen(false);
      return;
    }

    const checkStatus = async () => {
      // If skipped in this session, don't show
      if (sessionStorage.getItem("surveySkipped") === "true") {
        setOpen(false);
        return;
      }

      try {
        const response = await standardBizApiClient.survey_check_response();
        if (response && response.exists) {
          localStorage.setItem("surveyCompleted", "true");
          setOpen(false);
        } else {
          localStorage.removeItem("surveyCompleted");
          setOpen(true);
        }
      } catch (error) {
        console.error("Failed to check survey status", error);
        // Fallback to localStorage if API fails
        const completed = localStorage.getItem("surveyCompleted") === "true";
        if (!completed) setOpen(true);
      }
    };

    checkStatus();
  }, [isLoggedIn]);

  const handleComplete = () => {
    setOpen(false);
  };

  const handleSkip = () => {
    sessionStorage.setItem("surveySkipped", "true");
    setOpen(false);
  };

  return <SurveyModal open={open} onComplete={handleComplete} onSkip={handleSkip} />;
}
